import clsx from "clsx";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardList,
  faComment,
  faPlus,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type {
  AgentSummary,
  MissionDetail,
  MissionEntityRef,
} from "@/services/api/agentic-v2";
import { Badge } from "@/components/ui/badge/Badge";
import { Button } from "@/components/ui/button/Button";
import { OsLink } from "@/components/ui/os-layout";
import AgentWorkerAvatar from "@/components/worker/AgentWorkerAvatar";
import { entityRefLabel, isClientEntityType } from "../entityRefs";
import { formatEntityTypeLabel } from "../entityTypeDisplay";
import { formatRelativeTime } from "../MissionRow";
import { useResolvedMissionEntities } from "@/hooks/useResolvedMissionEntities";

/**
 * Compact mission toolbar.
 *
 * Layout (single row):
 *
 *   [entity pills]  Stage 2 · Starter Session Prep › Pre-meeting brief …
 *                                                      Updated 7m ago [avatars] [CTA]
 *
 * The OS window chrome already shows the mission title so we don't render
 * a second H1. Linked entities are inline pills (open the entity directly);
 * we only collapse into a popover when there are more than {@link MAX_INLINE_ENTITIES}.
 *
 * `agents` is the list of seeded agents pulled from /agentic-v2/agents,
 * filtered down to those in `mission.assigned_agents`.
 */
export interface MissionHeaderProps {
  mission: MissionDetail;
  agents: AgentSummary[];
  onAgentChat?: (agent: AgentSummary) => void;
  onDelegateTask?: () => void;
  onOpenAudit?: () => void;
}

const MAX_INLINE_ENTITIES = 3;

export function MissionHeader({
  mission,
  agents,
  onAgentChat,
  onDelegateTask,
  onOpenAudit,
}: MissionHeaderProps) {
  const assignedAgents = agents.filter((a) =>
    mission.assigned_agents.includes(a._id)
  );
  const stageLabel = currentStageLabel(mission);
  const laneLabel = currentLaneLabel(mission);
  const resolvedEntities = useResolvedMissionEntities(mission.entities);

  return (
    <header
      className="flex items-center gap-3 flex-wrap"
      data-testid="mission-header"
    >
      <EntityPills entities={resolvedEntities} />

      {stageLabel && (
        <span className="inline-flex items-baseline gap-1.5 text-sm min-w-0 truncate">
          <span className="text-slate-100 font-medium truncate">
            {stageLabel}
          </span>
          {laneLabel && (
            <>
              <span className="text-slate-600" aria-hidden>›</span>
              <span className="text-indigo-300 truncate">{laneLabel}</span>
            </>
          )}
        </span>
      )}

      <div className="flex-1" />

      <span className="text-xs text-slate-500 hidden md:inline-block">
        Updated {formatRelativeTime(mission.updated_at)}
      </span>

      <div className="flex items-center gap-2.5 shrink-0">
        {mission.kit_slug && onOpenAudit ? (
          <button
            type="button"
            onClick={onOpenAudit}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-white/[0.06] hover:text-slate-200 transition-colors"
            aria-label="Step audit log"
            data-testid="mission-step-audit-open"
          >
            <FontAwesomeIcon icon={faClipboardList} className="h-4 w-4" />
          </button>
        ) : null}
        <div className="flex h-9 items-center">
          <AgentDots agents={assignedAgents} onAgentChat={onAgentChat} />
        </div>
        {onDelegateTask && (
          <Button
            color="indigo"
            onClick={onDelegateTask}
            data-testid="mission-delegate-task"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Delegate task
          </Button>
        )}
      </div>
    </header>
  );
}

function currentStageLabel(mission: MissionDetail): string | null {
  const active = mission.stages.find((s) => s.name === mission.current_stage);
  if (!active) return mission.current_stage_display ?? null;
  return active.display_name;
}

function currentLaneLabel(mission: MissionDetail): string | null {
  if (mission.current_lane_display) return mission.current_lane_display;
  const active = mission.stages.find((s) => s.name === mission.current_stage);
  const lane = active?.lanes?.find((l) => l.key === mission.current_lane);
  return lane?.display_name ?? null;
}

// ── Entity pills ────────────────────────────────────────────────────

function EntityPills({ entities }: { entities: MissionEntityRef[] }) {
  if (entities.length === 0) return null;
  const inline = entities.slice(0, MAX_INLINE_ENTITIES);
  const overflow = entities.slice(MAX_INLINE_ENTITIES);
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="mission-entity-pills">
      {inline.map((entity, i) => (
        <EntityPill
          key={`${entity.entity_type ?? "e"}-${entity.entity_id ?? i}-${i}`}
          entity={entity}
        />
      ))}
      {overflow.length > 0 && <OverflowEntities entities={overflow} />}
    </div>
  );
}

const pillBaseClass =
  "inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full text-xs bg-slate-800/50 text-slate-200";

function EntityPill({ entity }: { entity: MissionEntityRef }) {
  const label = entityRefLabel(entity) || "Entity";
  const typeLabel = formatEntityTypeLabel(entity.entity_type);

  const isClient = isClientEntityType(entity.entity_type);
  const clientId = entity.entity_id;
  const openable = isClient && Boolean(clientId);

  const className = clsx(
    pillBaseClass,
    "max-w-[18rem]",
    openable && "hover:bg-white/[0.06] cursor-pointer",
  );

  const content = (
    <>
      <span className="truncate font-medium">{label}</span>
      {typeLabel && (
        <span className="text-[10px] text-slate-500 uppercase tracking-wide shrink-0">
          {typeLabel}
        </span>
      )}
    </>
  );

  if (openable && clientId) {
    return (
      <OsLink
        to={`/clients/${encodeURIComponent(clientId)}`}
        windowTitle={label}
        className={className}
        data-testid={`mission-entity-pill-${clientId}`}
      >
        {content}
      </OsLink>
    );
  }
  return (
    <span
      className={className}
      data-testid={`mission-entity-pill-${entity.entity_id ?? "n"}`}
    >
      {content}
    </span>
  );
}

function OverflowEntities({ entities }: { entities: MissionEntityRef[] }) {
  return (
    <Popover className="relative">
      <PopoverButton
        className={clsx(
          pillBaseClass,
          "hover:bg-white/[0.06] cursor-pointer focus:outline-none",
        )}
        aria-label={`${entities.length} more linked entities`}
        data-testid="mission-entity-overflow"
      >
        +{entities.length}
      </PopoverButton>
      <PopoverPanel
        anchor={{ to: "bottom start", gap: 8 }}
        className="z-50 w-80 max-w-[min(100vw-1rem,20rem)] rounded-xl bg-slate-900/95 backdrop-blur-xl shadow-2xl shadow-black/40 p-3 origin-top-left"
        transition
      >
        <p className="text-xs font-medium text-slate-400 mb-2">More linked entities</p>
        <ul className="space-y-2">
          {entities.map((entity, i) => (
            <li
              key={`${entity.entity_type ?? "e"}-${entity.entity_id ?? i}-${i}`}
              className="text-sm"
            >
              <span className="font-medium text-slate-100">
                {entityRefLabel(entity) || "Unnamed"}
              </span>
              {entity.entity_type && (
                <span className="text-slate-500 text-xs ml-2">
                  {formatEntityTypeLabel(entity.entity_type)}
                </span>
              )}
            </li>
          ))}
        </ul>
      </PopoverPanel>
    </Popover>
  );
}

interface AgentDotsProps {
  agents: AgentSummary[];
  onAgentChat?: (agent: AgentSummary) => void;
}

function AgentDots({ agents, onAgentChat }: AgentDotsProps) {
  if (agents.length === 0) {
    return (
      <span className="text-xs text-slate-600 italic">No agents assigned</span>
    );
  }
  return (
    <div className="flex items-center gap-1" data-testid="mission-agent-dots">
      {agents.map((agent) => (
        <AgentDot key={agent._id} agent={agent} onChat={onAgentChat} />
      ))}
    </div>
  );
}

/**
 * Agent presence dot with a Mac-style click-to-open popover card.
 * Phase 1: status is hardcoded "active" — we'll wire to real presence data
 * (heartbeat, current task) in Phase 2.
 */
function AgentDot({
  agent,
  onChat,
}: {
  agent: AgentSummary;
  onChat?: (agent: AgentSummary) => void;
}) {
  return (
    <Popover className="relative">
      <PopoverButton
        title={`${agent.name} · ${agent.role}`}
        aria-label={agent.name}
        data-testid={`agent-dot-${agent._id}`}
        className="relative rounded-full flex-shrink-0 hover:z-10 hover:scale-105 transition-transform focus:outline-none focus:z-10"
      >
        <AgentWorkerAvatar name={agent.name} avatarColour={agent.avatar_colour} size={28} />
      </PopoverButton>
      <PopoverPanel
        anchor={{ to: "bottom end", gap: 8 }}
        className="z-50 w-72 rounded-xl bg-slate-900/95 backdrop-blur-xl shadow-2xl shadow-black/40 p-3 origin-top-right"
        transition
      >
        {({ close }) => (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <AgentWorkerAvatar name={agent.name} avatarColour={agent.avatar_colour} size={40} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-100 truncate">
                  {agent.name}
                </p>
                <p className="text-xs text-slate-400 truncate">{agent.role}</p>
              </div>
              <Badge
                color={agent.status === "active" ? "green" : "slateMuted"}
                size="sm"
                className="shrink-0 uppercase tracking-wide"
              >
                {agent.status.replace(/_/g, " ")}
              </Badge>
            </div>

            {agent.description && (
              <p className="text-xs text-slate-400 leading-relaxed">
                {agent.description}
              </p>
            )}

            <button
              type="button"
              onClick={() => {
                onChat?.(agent);
                close();
              }}
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-500/15 text-indigo-200 text-xs font-medium hover:bg-indigo-500/25 transition-colors"
            >
              <FontAwesomeIcon icon={faComment} className="text-[10px]" />
              Chat with {agent.name}
            </button>
          </div>
        )}
      </PopoverPanel>
    </Popover>
  );
}
